import type {
  EquipmentItem,
  FixtureModel,
  Location,
  LocationRef,
  StocktakeDetail,
  StocktakeGroup,
  StocktakeLine,
  WorkingState,
} from './types'
import type { FieldValues } from './sync'

/**
 * What a record looks like on a device before the server has seen it
 * (issue #64, step 2).
 *
 * A mutation made offline is enqueued and reported to the caller as applied, so
 * the caches have to be able to say what the record now reads — otherwise the
 * walker types a count into a form that then shows them the old one. These are
 * the functions that do that, and they live here rather than in `apps/web` so
 * React Native adopts them with the queue.
 *
 * **This is provisional, and only ever provisional.** `InventoryPresenter` is
 * still the only thing that decides the wire format: the moment a changeset
 * syncs, the server's answer replaces what is here, `pending` goes, and the
 * record is the API's again. What these do is hold a plausible copy for the
 * hours in between — including a display name assembled the same way (issue
 * #59), which is the one rule reproduced here rather than read off an answer
 * that has not arrived.
 */

/**
 * `{brand} {name} - {mads code}`, with absent parts taking their separators
 * with them so it is never `Fresnel - `.
 *
 * The server's copy stays authoritative and overwrites this on sync; this is
 * for the record it has not been told about yet.
 */
export function provisionalItemName(
  brand: string | null,
  name: string,
  madsCode: string | null,
): string {
  const lead = [brand, name].filter((part) => part !== null && part !== '').join(' ')

  return madsCode !== null && madsCode !== '' ? `${lead} - ${madsCode}` : lead
}

export function provisionalModelName(brand: string | null, name: string): string {
  return [brand, name].filter((part) => part !== null && part !== '').join(' ')
}

/**
 * Ids for records the server has not given one to.
 *
 * Negative, so nothing collides with a real row and `id > 0` is all anybody
 * needs to ask to know whether the API has heard of this record — which is what
 * `useItem` and `useStocktake` guard their fetches on, and what Symfony's
 * `{id<\d+>}` would refuse outright.
 *
 * **Seeded from the clock, not from zero** (issue #70). A plain counter starts
 * again at −1 on every page load, and a device in a cellar reloads: the
 * persisted cache comes back holding a pending item at −1, the next record made
 * offline is handed −1 as well, and now two rows share a React key and a cache
 * entry. That was invisible while a pending row was only ever a line of text;
 * it stops being invisible the moment one can be opened. Milliseconds since the
 * epoch times a thousand, less a counter, is monotonic across reloads and
 * stays a long way inside `Number.MAX_SAFE_INTEGER`.
 */
let provisionalId = Date.now() * 1000

export function nextProvisionalId(): number {
  provisionalId += 1

  return -provisionalId
}

function str(fields: FieldValues, key: string): string | null {
  const value = fields[key]

  if (typeof value === 'string') return value.trim() === '' ? null : value.trim()

  return null
}

function num(fields: FieldValues, key: string): number | null {
  const value = fields[key]

  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value)
  }

  return null
}

/**
 * A brand-new item as it will read once it lands.
 *
 * `Not tested` rather than `Working` (issue #59): the queue must not assert on
 * a walker's behalf that kit nobody has checked is fine, and the server will
 * land it on exactly this.
 */
export function provisionalItem(
  clientId: string,
  fields: FieldValues,
  locations: readonly Location[],
): EquipmentItem {
  const category = (str(fields, 'category') ?? 'Cabling') as EquipmentItem['category']
  const isElectrical = category !== 'Cabling'
  const name = str(fields, 'name') ?? ''
  const brand = str(fields, 'brand')
  const madsCode = isElectrical ? str(fields, 'madsCode') : null

  return {
    id: nextProvisionalId(),
    clientId,
    pending: true,
    category,
    name,
    displayName: provisionalItemName(brand, name, madsCode),
    brand,
    notes: str(fields, 'notes'),
    quantity: num(fields, 'quantity') ?? 1,
    location: locationRef(fields, locations),
    madsCode,
    patTestCode: isElectrical ? str(fields, 'patTestCode') : null,
    patTestDate: isElectrical ? str(fields, 'patTestDate') : null,
    patStatus: isElectrical ? 'missing' : 'n/a',
    workingState: (isElectrical ? 'Not tested' : 'Not applicable') as WorkingState,
    fixtureModel: null,
    lightType: category === 'Lighting Fixture' ? str(fields, 'lightType') : null,
    lamp: category === 'Lighting Fixture' ? str(fields, 'lamp') : null,
    watts: category === 'Lighting Fixture' ? num(fields, 'watts') : null,
    lampOverridden: false,
    wattsOverridden: false,
    gelFrame: category === 'Lighting Fixture' ? str(fields, 'gelFrame') : null,
    goboFrame: category === 'Lighting Fixture' ? str(fields, 'goboFrame') : null,
    barnDoor: category === 'Lighting Fixture' ? str(fields, 'barnDoor') : null,
    attention: [],
    isElectrical,
    isLighting: category === 'Lighting Fixture',
    updatedAt: new Date().toISOString(),
    deletedAt: null,
    deletedBy: null,
  }
}

function locationRef(fields: FieldValues, locations: readonly Location[]): LocationRef | null {
  const value = fields['locationId']

  if (typeof value === 'number') {
    const found = locations.find((location) => location.id === value)

    return found === undefined ? null : { id: found.id, name: found.name }
  }

  // A shelf created in the same walk, still named by its client id. There is
  // nothing to look up yet, so the row simply reads as unassigned until sync
  // hands back both ids — which is truer than inventing a name for it.
  return null
}

/**
 * An item as it reads after an edit made offline.
 *
 * Only the fields the edit carried are touched, exactly as a `PATCH` is partial
 * — and the two rules that are easy to get wrong are honoured here as well as
 * on the server: reclassifying clears what the new category does not use, and
 * `null` on a lamp clears the override rather than being skipped.
 */
export function applyItemEdit(item: EquipmentItem, fields: FieldValues): EquipmentItem {
  const next: EquipmentItem = { ...item, pending: true, updatedAt: new Date().toISOString() }

  if ('category' in fields) {
    next.category = (str(fields, 'category') ?? item.category) as EquipmentItem['category']
    next.isElectrical = next.category !== 'Cabling'
    next.isLighting = next.category === 'Lighting Fixture'
  }

  if ('name' in fields) next.name = str(fields, 'name') ?? ''
  if ('brand' in fields) next.brand = str(fields, 'brand')
  if ('notes' in fields) next.notes = str(fields, 'notes')
  if ('quantity' in fields) next.quantity = num(fields, 'quantity') ?? item.quantity

  if (next.isElectrical) {
    if ('madsCode' in fields) next.madsCode = str(fields, 'madsCode')
    if ('patTestCode' in fields) next.patTestCode = str(fields, 'patTestCode')
    if ('patTestDate' in fields) next.patTestDate = str(fields, 'patTestDate')
    if ('workingState' in fields) {
      next.workingState = (str(fields, 'workingState') ?? item.workingState) as WorkingState
    }
  } else {
    // `EquipmentItem::setCategory()` drops what the new category does not use,
    // and a lantern turned into cabling must not keep a stale PAT record on the
    // screen either.
    next.madsCode = null
    next.patTestCode = null
    next.patTestDate = null
    next.patStatus = 'n/a'
    next.workingState = 'Not applicable'
  }

  if (next.isLighting) {
    if ('lamp' in fields) {
      // Absent and null are different things here: null is "clear the override
      // and go back to reading the model" (issue #59).
      next.lamp = str(fields, 'lamp')
      next.lampOverridden = next.lamp !== null
    }
    if ('watts' in fields) {
      next.watts = num(fields, 'watts')
      next.wattsOverridden = next.watts !== null
    }
    if (item.fixtureModel === null) {
      if ('lightType' in fields) next.lightType = str(fields, 'lightType')
      if ('gelFrame' in fields) next.gelFrame = str(fields, 'gelFrame')
      if ('goboFrame' in fields) next.goboFrame = str(fields, 'goboFrame')
      if ('barnDoor' in fields) next.barnDoor = str(fields, 'barnDoor')
    }
  } else {
    next.lightType = null
    next.lamp = null
    next.watts = null
    next.gelFrame = null
    next.goboFrame = null
    next.barnDoor = null
    next.lampOverridden = false
    next.wattsOverridden = false
  }

  next.displayName = provisionalItemName(next.brand, next.name, next.madsCode)

  return next
}

export function provisionalLocation(clientId: string, fields: FieldValues): Location {
  return {
    id: nextProvisionalId(),
    clientId,
    pending: true,
    name: str(fields, 'name') ?? '',
    description: str(fields, 'description'),
    position: Number.MAX_SAFE_INTEGER,
    itemCount: 0,
    unitCount: 0,
    deletedAt: null,
    deletedBy: null,
  }
}

export function applyLocationEdit(location: Location, fields: FieldValues): Location {
  return {
    ...location,
    pending: true,
    name: 'name' in fields ? (str(fields, 'name') ?? location.name) : location.name,
    description: 'description' in fields ? str(fields, 'description') : location.description,
  }
}

export function provisionalFixtureModel(clientId: string, fields: FieldValues): FixtureModel {
  const name = str(fields, 'name') ?? ''
  const brand = str(fields, 'brand')

  return {
    id: nextProvisionalId(),
    clientId,
    pending: true,
    name,
    displayName: provisionalModelName(brand, name),
    brand,
    lightType: str(fields, 'lightType'),
    lamp: str(fields, 'lamp'),
    watts: num(fields, 'watts'),
    gelFrame: str(fields, 'gelFrame'),
    goboFrame: str(fields, 'goboFrame'),
    barnDoor: str(fields, 'barnDoor'),
    notes: str(fields, 'notes'),
    links: [],
    itemCount: 0,
    unitCount: 0,
    updatedAt: new Date().toISOString(),
  }
}

// — stocktakes (issue #64, step 3) —

/**
 * A walk as it reads on the device that started it, before the server has one.
 *
 * The line set is built here from the kit the device has cached, which is the
 * same thing `StocktakeMutator::start()` does from the items it holds — and the
 * two can legitimately differ, because the device's copy is as of whenever it
 * last had a signal. That difference is not a problem to be prevented: the
 * sheet the walker carries round is this one, and every count they take names
 * its own item, so it lands whether or not the server expected that line.
 *
 * `locationIds` empty means the whole store in walking order, exactly as the
 * endpoint's empty `locationIds` does.
 */
export function provisionalStocktake(
  clientId: string,
  locationIds: readonly number[],
  items: readonly EquipmentItem[],
  locations: readonly Location[],
  startedBy: string | null,
): StocktakeDetail {
  const live = locations
    .filter((location) => location.deletedAt === null)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))

  const walked = locationIds.length === 0 ? live : live.filter((l) => locationIds.includes(l.id))
  const walkedIds = new Set(walked.map((location) => location.id))

  const groups: StocktakeGroup[] = walked.map((location) => ({
    location: { id: location.id, name: location.name },
    lines: items
      .filter((item) => item.deletedAt === null && item.location?.id === location.id)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(provisionalLine),
  }))

  const total = groups.reduce((sum, group) => sum + group.lines.length, 0)

  return {
    id: nextProvisionalId(),
    clientId,
    pending: true,
    status: 'in_progress',
    startedAt: new Date().toISOString(),
    completedAt: null,
    startedBy,
    locations: walked
      .filter((location) => walkedIds.has(location.id))
      .map((location) => ({ id: location.id, name: location.name })),
    totalLines: total,
    countedLines: 0,
    discrepancies: 0,
    groups,
  }
}

/**
 * A line's id is provisional too, and negative for the same reason a record's
 * is: nothing on the device may be mistaken for a row. What names the line to
 * the server is `itemId`, which is real.
 */
function provisionalLine(item: EquipmentItem): StocktakeLine {
  return {
    id: nextProvisionalId(),
    itemId: item.id,
    name: item.name,
    displayName: item.displayName,
    brand: item.brand,
    category: item.category,
    madsCode: item.madsCode,
    expectedQuantity: item.quantity,
    countedQuantity: null,
    counted: false,
    discrepancy: false,
    note: null,
  }
}

/**
 * A walk as it reads after a count taken offline.
 *
 * The tallies are recomputed rather than incremented, because a walker
 * correcting a number they mistyped would otherwise count twice — and because
 * a recount that agrees with the record has to *remove* a discrepancy.
 */
export function applyLineCount(
  detail: StocktakeDetail,
  itemId: number,
  changes: { countedQuantity?: number | null; note?: string | null },
): StocktakeDetail {
  const groups = detail.groups.map((group) => ({
    ...group,
    lines: group.lines.map((line) => (line.itemId === itemId ? countedLine(line, changes) : line)),
  }))

  const lines = groups.flatMap((group) => group.lines)

  return {
    ...detail,
    groups,
    countedLines: lines.filter((line) => line.counted).length,
    discrepancies: lines.filter((line) => line.discrepancy).length,
  }
}

function countedLine(
  line: StocktakeLine,
  changes: { countedQuantity?: number | null; note?: string | null },
): StocktakeLine {
  // Partial, like the PATCH it stands in for: a note must not blank a count.
  const counted =
    'countedQuantity' in changes ? (changes.countedQuantity ?? null) : line.countedQuantity
  const note = 'note' in changes ? (changes.note === '' ? null : (changes.note ?? null)) : line.note

  return {
    ...line,
    countedQuantity: counted,
    counted: counted !== null,
    discrepancy: counted !== null && counted !== line.expectedQuantity,
    note,
  }
}

export function applyFixtureModelEdit(model: FixtureModel, fields: FieldValues): FixtureModel {
  const next: FixtureModel = { ...model, pending: true }

  if ('name' in fields) next.name = str(fields, 'name') ?? model.name
  if ('brand' in fields) next.brand = str(fields, 'brand')
  if ('lightType' in fields) next.lightType = str(fields, 'lightType')
  if ('lamp' in fields) next.lamp = str(fields, 'lamp')
  if ('watts' in fields) next.watts = num(fields, 'watts')
  if ('gelFrame' in fields) next.gelFrame = str(fields, 'gelFrame')
  if ('goboFrame' in fields) next.goboFrame = str(fields, 'goboFrame')
  if ('barnDoor' in fields) next.barnDoor = str(fields, 'barnDoor')
  if ('notes' in fields) next.notes = str(fields, 'notes')

  next.displayName = provisionalModelName(next.brand, next.name)

  return next
}
